import { useIntl } from "react-intl";
import * as Yup from "yup";
import { forwardRef, useImperativeHandle, useState } from "react";
import axios from "axios";
import { getIn, useFormik } from "formik";
import clsx from "clsx";
import ReactInputMask from "react-input-mask";
import PhoneNumberInput from "./components/fields/InputCelphone";
import { fieldsInterface, InputColorInterface } from "./FormModel";
import { useStyleContext } from "../../helpers/StyleContext";
import { toastfy } from "../toast/Toast";
import { showErrorMessage } from "../../helpers/MessagesHelper";
import { getAuth } from "../../../app/modules/auth";
import WebCamPicture from "../../layout/components/WebCamPicture/WebCamPicture";
import { InputText } from "./components/fields/InputText";
import { InputCheckbox } from "./components/fields/InputCheckbox";
import SwitchStatus from "./components/fields/SwitchStatus";
import SwitchBan from "./components/fields/SwitchBan";

interface PropsEditModal {
  _user_id: number;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface FormHandlesEditUsersModal {
  handleSubmit: () => void;
  fetchData: () => void;
}

type CustomFieldInterface = fieldsInterface & {
  colClass?: string;
  disabled?: boolean;
};

const UsersEditModal: React.ForwardRefRenderFunction<
  FormHandlesEditUsersModal,
  PropsEditModal
> = ({ _user_id, setIsLoading }, ref) => {
  const intl = useIntl();
  const module = "users";
  const operation = "users/update";
  const [isLoadingForm, setIsLoadingForm] = useState(true);

  const fields: CustomFieldInterface[] = [
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.IMAGEM",
      })}`,
      reference: "profile_image",
      field: {
        element: "profile_image",
      },
      required: true,
      colClass: "col-12",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.NAME",
      })}`,
      reference: "first_name",
      readOnly: true,
      field: {
        element: "input",
        type: "text",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.NAME.PLACEHOLDER",
        })}`,
      },
      required: true,
      colClass: "col-md-6",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.LASTNAME",
      })}`,
      reference: "last_name",
      readOnly: true,
      field: {
        element: "input",
        type: "text",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.LASTNAME.PLACEHOLDER",
        })}`,
      },
      required: true,
      colClass: "col-md-6",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.CPF",
      })}`,
      reference: "cpf",
      readOnly: true,
      field: {
        element: "input",
        type: "cpf",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.CPF.PLACEHOLDER",
        })}`,
      },
      required: true,
      colClass: "col-md-4",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.CELPHONE",
      })}`,
      reference: "celphone",
      field: {
        element: "input",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.CELPHONE.PLACEHOLDER",
        })}`,
      },
      required: true,
      colClass: "col-md-4",
    },
    {
      description: "E-mail",
      reference: "email",
      readOnly: true,
      field: {
        element: "input",
        type: "email",
        id: "email",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.EMAIL.PLACEHOLDER",
        })}`,
      },
      required: true,
      colClass: "col-md-4",
    },
    {
      description: `${intl.formatMessage({
        id: "MODULE.FIELDSINTERFACE.INSTAGRAM",
      })}`,
      reference: "instagram",
      field: {
        type: "instagram",
        element: "input",
        id: "instagram",
        placeholder: `${intl.formatMessage({
          id: "MODULE.FIELDSINTERFACE.INSTAGRAM.PLACEHOLDER",
        })}`,
      },
      required: false,
      colClass: "col-md-6",
    },
  ];

  const validationSchema = Yup.object().shape({
    profile_image: Yup.string().required(
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_IMAGEM",
      })}`,
    ),
    celphone: Yup.string().test(
      "celphone",
      `${intl.formatMessage({
        id: "MODULE.VALIDATIONSCHEMA.REQUIRED_CELPHONE_INVALID",
      })}`,
      (value) => {
        if (!value) return true;
        const numericValue = value.replace(/\D/g, "");
        return numericValue.length === 11;
      },
    ),
    email: Yup.string()
      .email(
        `${intl.formatMessage({
          id: "MODULE.VALIDATIONSCHEMA.EMAIL_INVALID_FORMAT",
        })}`,
      )
      .min(
        3,
        `${intl.formatMessage({
          id: "MODULE.VALIDATIONSCHEMA.MIN_CHARACTERS",
        })}`,
      )
      .max(
        50,
        `${intl.formatMessage({
          id: "MODULE.VALIDATIONSCHEMA.MAX_CHARACTERS",
        })}`,
      )
      .required(
        `${intl.formatMessage({
          id: "MODULE.VALIDATIONSCHEMA.REQUIRED_EMAIL",
        })}`,
      ),
    instagram: Yup.string()
      .nullable()
      .min(
        3,
        `${intl.formatMessage({
          id: "MODULE.VALIDATIONSCHEMA.REQUIRED_LAST_NAME_MIN_CHARACTERS",
        })}`,
      ),
  });

  const initialValues: { [key: string]: any } = fields.reduce(
    (acc, field) => ({ ...acc, [field.reference]: "" }),
    {},
  );

  const [clearPicture, setClearPicture] = useState(false);
  const handlePictureCapture = (picture: string | Blob) => {
    formik.setFieldValue("profile_image", picture);
  };
  const handleClearPicture = () => {
    setClearPicture(!clearPicture);
  };

  const [showPicture, setShowPicture] = useState(false);
  const toggleShowPicture = () => {
    setShowPicture((prevState) => !prevState);
  };

  const [elementsInputColor] = useState<InputColorInterface[]>([]);
  const { styleState } = useStyleContext();

  // 🟢 Função para tratar URLs da imagem e prevenir barras duplicadas
  const getProfileImageUrl = (imagePath: string | null | undefined) => {
    if (!imagePath) return "/media/avatars/blank.png";
    if (imagePath.startsWith("data:image") || imagePath.startsWith("http")) {
      return imagePath;
    }
    const cleanPath = imagePath.startsWith("/") ? imagePath.slice(1) : imagePath;
    return `https://companies-admin.aacceson.com/${cleanPath}`;
  };

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    setIsLoading(true);

    const inputsCheckbox = document.querySelectorAll("input[type=checkbox]");

    const formatFields = (fields: fieldsInterface[], body: any) => {
      const formattedBody = { ...body };
      fields.forEach((field) => {
        if (field.field.type === "number") {
          formattedBody[field.reference] = Number(body[field.reference]);
        }
      });

      return formattedBody;
    };

    const mapCheckboxElements = Array.from(inputsCheckbox).map((element) => {
      const checkboxElement = element as HTMLInputElement;

      return {
        [checkboxElement.name]: checkboxElement.checked,
      };
    });

    const formattedCheckboxElements = mapCheckboxElements?.reduce(
      (acc, obj) => ({ ...acc, ...obj }),
      {},
    );

    const mapElementsInputColor = elementsInputColor?.map((element) => {
      return {
        [element.reference]: element.value,
      };
    });

    const formattedInputColorElements = mapElementsInputColor?.reduce(
      (acc, obj) => ({ ...acc, ...obj }),
      {},
    );

    const formattedBody = {
      ...formatFields(fields, values),
      ...formattedCheckboxElements,
      ...formattedInputColorElements,
    };

    const apiUrl = process.env.REACT_APP_API_URL;

    const headers = {
      Authorization: `Bearer ${token}`,
      operation: operation,
    };

    const uploadImage = ({ formData, headers }: any) => {
      axios
        .put(`${apiUrl}/profile${_user_id ? `/${_user_id}` : ""}`, formData, {
          headers,
        })
        .then((response) => {})
        .catch((error) => {});
    };

    const formData = new FormData();
    formData.append("profile_image", formik.values.profile_image);
    axios
      .put(
        `${apiUrl}/${module}/edit${_user_id ? `/${_user_id}` : ""}`,
        formattedBody,
        {
          headers,
        },
      )
      .then((response) => {
        setIsLoading(false);

        toastfy("success", "Operação bem sucedida!");
        formik.resetForm();
        handleClearPicture();

        if (module === "users") {
          const hasProfileImageField = fields?.some(
            (field) => field?.field?.element === "profile_image",
          );

          hasProfileImageField && uploadImage({ formData, headers });
        }
      })
      .catch((error) => {
        showErrorMessage(error?.response?.status, error?.response?.data?.error);

        const timeoutId = setTimeout(() => {
          setIsLoading(false);
        }, 1000);

        return () => clearTimeout(timeoutId);
      });
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const apiToken = getAuth();
  const token = apiToken?.api_token;
  const apiUrl = process.env.REACT_APP_API_URL;
  const headers = {
    Authorization: `Bearer ${token}`,
    operation: `${module}/find-by-id`,
  };

  const fetchData = async () => {
    try {
      await axios
        .get(`${apiUrl}/${module}/${_user_id}`, {
          headers,
        })
        .then((response) => {
          const data = response.data;

          setIsLoadingForm(false);

          Object.keys(data).forEach((key) => {
            if (data[key] && data[key].description) {
              data[key] = data[key].description;
            }
          });

          formik.setValues(data);
        })
        .catch((error) => {
          showErrorMessage(
            error?.response?.data?.error?.statusCode,
            error?.response?.data?.error?.message,
          );
        });
    } catch (error) {
      console.error(error);
      setIsLoadingForm(false);
      toastfy("warning", "Erro ao carregar dados do usuário!");
    }
  };

  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      formik.handleSubmit();
    },
    fetchData: () => {
      fetchData();
    },
  }));

  const getUserType = (containerId: string | undefined) => {
    if (!containerId) return "Não Definido";
    const types: Record<string, string> = {
      "89b33a5d8f6d610c43cdb54359bdc122": "Visitante",
      "e2f7330728b5eacaef4919ee42bd646d": "Patrocinador",
      "deecad80a2b97f02377b213b3e8e2021": "Funcionário",
      "755b46b53cb5f23fc6c7f8fb807850a5": "Comissão",
      "8807d8ab4397779bf10e9f65873996ba": "Atleta",
      "3bd596d38e21735d4eb4448dc39a7644": "Associado",
      "91f5167c34c400758115c2a6826ec2e3": "Administrador",
    };
    return (
      types[containerId] || types[containerId.toLowerCase()] || "Desconhecido"
    );
  };

  const renderField = (field: CustomFieldInterface) => {
    if (!field) return null;

    const isReadOnlyOrDisabled =
      field.readOnly === true || field.disabled === true;
    const disabledStyle = isReadOnlyOrDisabled
      ? { backgroundColor: "#ecedf3" }
      : {};

    const wrapperDisabledStyle = isReadOnlyOrDisabled
      ? { pointerEvents: "none" as const, opacity: 0.6 }
      : {};

    return (
      <>
        <label
          className={
            "form-label fs-6 fw-bold mb-1 " +
            (field.required && !isReadOnlyOrDisabled ? "required" : "")
          }
          style={{
            color: styleState?.secondary_text_color,
          }}
          htmlFor={field.reference}
        >
          {field.description}
        </label>
        <div className="w-100">
          {field.field.element === "input" &&
            (field.field.type === "text" ||
              field.field.type === "number" ||
              field.field.type === "instagram" ||
              field.field.type === "email") && (
              <InputText
                className={clsx(
                  "form-control form-control-sm",
                  {
                    "is-invalid":
                      getIn(formik?.touched, field.reference) &&
                      getIn(formik?.errors, field.reference),
                  },
                  {
                    "is-valid":
                      getIn(formik?.touched, field.reference) &&
                      !getIn(formik?.errors, field.reference),
                  },
                )}
                style={disabledStyle}
                readOnly={field.readOnly}
                disabled={field.disabled}
                name={field.reference}
                placeholder={field.field.placeholder}
                value={getIn(formik?.values, field.reference) ?? ""}
                handleChange={formik.handleChange}
                onBlur={formik.handleBlur}
                id={field.reference}
              />
            )}

          {field.field.element === "input" && (
            <>
              {field.reference === "celphone" && (
                <div style={wrapperDisabledStyle}>
                  <PhoneNumberInput
                    className={clsx(
                      "form-control form-control-sm",
                      {
                        "is-invalid":
                          getIn(formik?.touched, field.reference) &&
                          getIn(formik?.errors, field.reference),
                      },
                      {
                        "is-valid":
                          getIn(formik?.touched, field.reference) &&
                          !getIn(formik?.errors, field.reference),
                      },
                    )}
                    name={field.reference}
                    placeholder={field.field.placeholder}
                    value={getIn(formik?.values, field.reference)}
                    onChange={(value: string) => {
                      formik.setFieldValue(field.reference, value);
                    }}
                  />
                </div>
              )}
              {field.field.type === "cpf" && (
                <ReactInputMask
                  style={disabledStyle}
                  readOnly={field.readOnly}
                  disabled={field.disabled}
                  mask="999.999.999-99"
                  className={clsx(
                    "form-control form-control-sm",
                    {
                      "is-invalid":
                        getIn(formik?.touched, field.reference) &&
                        getIn(formik?.errors, field.reference),
                    },
                    {
                      "is-valid":
                        getIn(formik?.touched, field.reference) &&
                        !getIn(formik?.errors, field.reference),
                    },
                  )}
                  name={field.reference}
                  id={field.field.id}
                  placeholder={field.field.placeholder}
                  value={getIn(formik?.values, field.reference)}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              )}
            </>
          )}

          {field.field.type === "checkbox" && (
            <div style={wrapperDisabledStyle}>
              <InputCheckbox
                className={field.className}
                name={field.reference}
                id={field.field.id}
                defaultValue={formik.values[field.reference] || false}
              />
            </div>
          )}

          {field.field.element === "switch_status" && (
            <div style={wrapperDisabledStyle}>
              <SwitchStatus
                itemId={Number(formik.values?._id_user)}
                active={formik.values.status === "available" ? true : false}
                module={module}
                token={token}
                apiUrl={apiUrl}
                setDashboardUpdate={() => {}}
              />
            </div>
          )}

          {field.field.element === "switch_banned" && (
            <div style={wrapperDisabledStyle}>
              <SwitchBan
                itemId={Number(formik.values?._id_user)}
                active={formik.values.banned ? true : false}
                module={module}
                token={token}
                apiUrl={apiUrl}
              />
            </div>
          )}

          {field.field.element === "profile_image" && (
            <div key={field.description}>
              {showPicture && !isReadOnlyOrDisabled ? (
                <WebCamPicture
                  setClearPicture={setClearPicture}
                  clearPicture={clearPicture}
                  onPictureCapture={handlePictureCapture}
                  className={clsx(
                    {
                      "is-invalid":
                        getIn(formik?.touched, field.reference) &&
                        getIn(formik?.errors, field.reference),
                    },
                    {
                      "is-valid":
                        getIn(formik?.touched, field.reference) &&
                        !getIn(formik?.errors, field.reference),
                    },
                  )}
                />
              ) : (
                <div
                  className="form-control form-control-sm text-center py-4"
                  style={disabledStyle}
                >
                  <img
                    src={getProfileImageUrl(formik?.values?.profile_image)}
                    alt="Profile"
                    style={{
                      width: "7em",
                      height: "7em",
                      borderRadius: "7em",
                      border: "5px solid white",
                    }}
                  />
                  {!isReadOnlyOrDisabled && (
                    <button
                      className="btn btn-danger btn-sm ms-3"
                      title="Excluir Imagem"
                      type="button"
                      onClick={toggleShowPicture}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-x-lg"
                        viewBox="0 0 16 16"
                      >
                        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {getIn(formik.touched, field.reference) &&
            getIn(formik.errors, field.reference) && (
              <div className="fv-plugins-message-container mt-1">
                <div className="fv-help-block text-danger">
                  <span role="alert">
                    {getIn(formik.errors, field.reference)}
                  </span>
                </div>
              </div>
            )}
        </div>
      </>
    );
  };

  return (
    <div className="text-center">
      {!isLoadingForm ? (
        <form onSubmit={formik.handleSubmit}>
          {/* 🟢 NOVOS CAMPOS: STATUS, BANIDO E TIPO DE USUÁRIO COMO BADGES */}
          <div className="row mb-2">
            <div className="col-md-1 my-2 text-start">
              <label
                className="form-label fs-6 fw-bold mb-1"
                style={{ color: styleState?.secondary_text_color }}
              >
                Status
              </label>
              <div className="w-100 mt-1">
                <span
                  style={{
                    width: "100%",
                  }}
                  className={clsx(
                    "badge fs-6 px-4 py-2",
                    formik.values?.status === "available"
                      ? "bg-success"
                      : "bg-danger",
                  )}
                >
                  {formik.values?.status === "available" ? "Ativo" : "Inativo"}
                </span>
              </div>
            </div>

            <div className="col-md-1 my-2 text-start">
              <label
                className="form-label fs-6 fw-bold mb-1"
                style={{ color: styleState?.secondary_text_color }}
              >
                Banido
              </label>
              <div className="w-100 mt-1">
                <span
                  style={{
                    width: "100%",
                  }}
                  className={clsx(
                    "badge fs-6 px-4 py-2",
                    formik.values?.banned ? "bg-danger" : "bg-success",
                  )}
                >
                  {formik.values?.banned ? "Sim" : "Não"}
                </span>
              </div>
            </div>

            <div className="col-md-2 my-2 text-start">
              <label
                className="form-label fs-6 fw-bold mb-1"
                style={{ color: styleState?.secondary_text_color }}
              >
                Tipo de Usuário
              </label>
              <div className="w-100 mt-1">
                <span
                  style={{
                    width: "100%",
                    backgroundColor: formik.values?.bracelet?.cor_da_pulseira?.cor || undefined,
                    color: formik.values?.bracelet?.cor_da_pulseira?.cor === "#ffffff" || formik.values?.bracelet?.cor_da_pulseira?.cor === "#FFFFFF" ? "#000000" : "#ffffff",
                    border: formik.values?.bracelet?.cor_da_pulseira?.cor === "#ffffff" || formik.values?.bracelet?.cor_da_pulseira?.cor === "#FFFFFF" ? "1px solid #d8d8d8" : "none",
                  }}
                  className={clsx(
                    "badge fs-6 px-4 py-2",
                    !formik.values?.bracelet?.cor_da_pulseira?.cor && "bg-primary"
                  )}
                >
                  {formik.values?.bracelet?.cor_da_pulseira?.nome || getUserType(formik.values?._container_id)}
                </span>
              </div>
            </div>
          </div>
          <div className="row align-items-center mb-4">
            <div className="col-md-3 text-center">
              {renderField(
                fields.find(
                  (f) => f.reference === "profile_image",
                ) as CustomFieldInterface,
              )}
            </div>

            <div className="col-md-9">
              <div className="row mb-2">
                <div className="col-md-6 text-start">
                  {renderField(
                    fields.find(
                      (f) => f.reference === "first_name",
                    ) as CustomFieldInterface,
                  )}
                </div>
                <div className="col-md-6 text-start">
                  {renderField(
                    fields.find(
                      (f) => f.reference === "last_name",
                    ) as CustomFieldInterface,
                  )}
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-md-3 text-start">
                  {renderField(
                    fields.find(
                      (f) => f.reference === "celphone",
                    ) as CustomFieldInterface,
                  )}
                </div>
                <div className="col-md-6 text-start">
                  {renderField(
                    fields.find(
                      (f) => f.reference === "email",
                    ) as CustomFieldInterface,
                  )}
                </div>
                <div className="col-md-3 text-start">
                  {renderField(
                    fields.find(
                      (f) => f.reference === "cpf",
                    ) as CustomFieldInterface,
                  )}
                </div>
              </div>
            </div>
          </div>

          <hr className="text-muted" />

          <div className="row mt-3">
            {fields
              .filter(
                (f) =>
                  ![
                    "profile_image",
                    "first_name",
                    "last_name",
                    "celphone",
                    "email",
                    "cpf",
                  ].includes(f.reference),
              )
              .map((field, index) => (
                <div
                  className={clsx(
                    field.colClass || "col-md-4",
                    "my-2 text-start",
                  )}
                  key={`${field.description}-${index}`}
                >
                  {renderField(field)}
                </div>
              ))}
          </div>
        </form>
      ) : (
        <span className="spinner-border spinner-border-xl align-middle ms-2"></span>
      )}
    </div>
  );
};

export default forwardRef(UsersEditModal);